import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { products as initialProducts } from '../data/products';
import stripeService from '../services/stripeService';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextId, setNextId] = useState(1);
  const [availableCategories, setAvailableCategories] = useState(['Tous']);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fonction pour mettre à jour les catégories disponibles
  const updateAvailableCategories = useCallback(() => {
    // Récupérer les catégories depuis localStorage (catégories personnalisées)
    const savedCategories = localStorage.getItem('productCategories');
    let customCategories = [];
    
    if (savedCategories) {
      try {
        customCategories = JSON.parse(savedCategories);
        // console.log('📂 Catégories personnalisées chargées:', customCategories);
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err);
      }
    }
    
    // Combiner les catégories des produits et les catégories personnalisées
    const productCategories = [...new Set(products.map(product => product.category))];
    const allCategories = [...new Set([...customCategories, ...productCategories])];
    
    const result = ['Tous', ...allCategories.sort()];
    
    // Éviter les mises à jour inutiles en comparant avec l'état actuel
    setAvailableCategories(prevCategories => {
      if (JSON.stringify(prevCategories) !== JSON.stringify(result)) {
        console.log('📂 Toutes les catégories disponibles:', result);
        return result;
      }
      return prevCategories;
    });
  }, [products]);

  // Synchroniser le stock avec le serveur et récupérer le stock à jour
  const syncStockWithServer = async (productsToSync) => {
    try {
      // Importer stripeService dynamiquement
      const stripeService = (await import('../services/stripeService')).default;
      
      // Synchroniser le stock initial avec le serveur
      const serverStock = await stripeService.syncProductsStock(productsToSync);
      
      if (serverStock && Object.keys(serverStock).length > 0) {
        // Mettre à jour les produits avec le stock du serveur
        setProducts(prevProducts => {
          const updated = prevProducts.map(product => {
            const serverProductStock = serverStock[product.id];
            if (serverProductStock !== undefined) {
              return {
                ...product,
                stock: serverProductStock.stock
              };
            }
            return product;
          });
          return updated;
        });
        console.log('✅ Stock synchronisé avec le serveur');
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la synchronisation du stock avec le serveur:', error);
      // Ne pas bloquer si le serveur n'est pas accessible
    }
  };

  // Synchroniser automatiquement avec Stripe au démarrage (optimisé, non bloquant)
  const syncProductsWithStripe = async (productsToSync) => {
    try {
      // Déterminer l'URL du backend
      const backendUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:4242'
        : window.location.origin;
      
      console.log(`🔍 Vérification serveur backend: ${backendUrl}`);
      
      // Vérifier d'abord si le serveur est accessible (timeout court avec AbortController)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      let healthCheck = null;
      try {
        healthCheck = await fetch(`${backendUrl}/`, {
          method: 'GET',
          signal: controller.signal
        });
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          console.info('ℹ️ Le serveur backend ne répond pas encore (peut être en cours de démarrage)');
          console.info('💡 La synchronisation Stripe sera réessayée automatiquement. Les produits fonctionnent quand même.');
        } else {
          console.info('ℹ️ Serveur backend non accessible pour le moment:', fetchError.message);
          console.info('💡 Les produits fonctionnent sans synchronisation. Lancez le serveur avec: npm run server');
        }
        // Ne pas considérer cela comme une erreur - c'est normal si le serveur n'est pas démarré
        return productsToSync;
      }
      
      clearTimeout(timeoutId);
      
      if (!healthCheck || !healthCheck.ok) {
        console.warn(`⚠️ Serveur backend répond avec erreur (${healthCheck?.status || 'inconnu'}), synchronisation Stripe reportée`);
        return productsToSync;
      }
      
      console.log('✅ Serveur backend accessible, démarrage de la synchronisation...');
      
      console.log('🚀 Synchronisation automatique des produits avec Stripe...');
      console.log(`📦 ${productsToSync.length} produits à synchroniser`);
      
      // Synchroniser avec timeout pour éviter de bloquer
      const syncPromise = stripeService.syncAllProducts(productsToSync);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout sync')), 30000) // 30 secondes max
      );
      
      const result = await Promise.race([syncPromise, timeoutPromise]);
      
      if (result && result.length > 0) {
        // Fusionner les produits synchronisés avec ceux en mémoire
        const updatedProducts = productsToSync.map(localProduct => {
          const syncedProduct = result.find(sp => sp.id === localProduct.id);
          if (syncedProduct) {
            // Utiliser le produit synchronisé avec les IDs Stripe
            return {
              ...localProduct,
              stripeProductId: syncedProduct.stripeProductId,
              stripePriceId: syncedProduct.stripePriceId,
              syncedAt: syncedProduct.syncedAt
            };
          }
          return localProduct;
        });
        
        // Mettre à jour l'état avec les produits synchronisés
        setProducts(updatedProducts);
        localStorage.setItem('farmProducts', JSON.stringify(updatedProducts));
        console.log(`✅ ${result.length} produits synchronisés avec Stripe`);
        return updatedProducts;
      }
      
      return productsToSync;
      } catch (error) {
        if (error.message !== 'Timeout sync') {
          console.warn('⚠️ Erreur lors de la synchronisation automatique Stripe:', error.message);
          console.info('💡 Les produits fonctionneront quand même. La synchronisation sera réessayée automatiquement.');
        } else {
          console.warn('⚠️ Synchronisation Stripe timeout - les produits fonctionneront quand même');
          console.info('💡 Les IDs Stripe seront créés lors du premier paiement si nécessaire.');
        }
        // Ne pas bloquer le démarrage si la synchronisation échoue
        // Les produits fonctionneront sans IDs Stripe (création à la volée lors du paiement)
        return productsToSync;
      }
  };

  // Initialiser les produits
  useEffect(() => {
    const initializeProducts = async () => {
      const savedProducts = localStorage.getItem('farmProducts');
      let loadedProducts;
      
      if (savedProducts) {
        try {
          const parsedProducts = JSON.parse(savedProducts);
          loadedProducts = parsedProducts;
          setNextId(Math.max(...parsedProducts.map(p => p.id)) + 1);
          console.log('📦 Produits chargés depuis localStorage:', parsedProducts.length);
        } catch (err) {
          console.error('Erreur lors du chargement des produits:', err);
          loadedProducts = initialProducts;
          setNextId(Math.max(...initialProducts.map(p => p.id)) + 1);
        }
      } else {
        loadedProducts = initialProducts;
        setNextId(Math.max(...initialProducts.map(p => p.id)) + 1);
        console.log('📦 Produits initiaux chargés:', initialProducts.length);
      }
      
      // Synchroniser avec Stripe en arrière-plan (ne bloque pas le chargement)
      setProducts(loadedProducts);
      setIsLoading(false);
      setIsInitialized(true);
      
      // Synchroniser avec Stripe de manière asynchrone (ne bloque pas le chargement)
      // Délai plus long pour laisser le serveur backend démarrer complètement
      const syncDelay = 3000; // 3 secondes pour laisser le serveur démarrer
      
      // Utiliser requestIdleCallback si disponible pour ne pas bloquer l'UI
      const startSync = async () => {
        console.log('🔄 Synchronisation Stripe en arrière-plan...');
        try {
          const syncedProducts = await syncProductsWithStripe(loadedProducts);
          setProducts(syncedProducts);
        } catch (error) {
          console.warn('⚠️ Erreur lors de la synchronisation en arrière-plan:', error.message);
          // Ne pas mettre à jour les produits si la sync échoue
        }
      };
      
      if (window.requestIdleCallback) {
        requestIdleCallback(startSync, { timeout: syncDelay + 2000 });
      } else {
        setTimeout(startSync, syncDelay);
      }
      
      // Mettre à jour les catégories après le chargement des produits
      setTimeout(() => updateAvailableCategories(), 100);
      
      // Synchroniser le stock avec le serveur (pour avoir le stock à jour partagé)
      if (loadedProducts && loadedProducts.length > 0) {
        syncStockWithServer(loadedProducts);
      }
    };
    
    initializeProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Écouter les événements de synchronisation
  useEffect(() => {
    const handleProductsUpdate = async (event) => {
      console.log('🔄 Événement de synchronisation reçu:', event.detail);
      // Recharger les produits depuis localStorage
      const savedProducts = localStorage.getItem('farmProducts');
      if (savedProducts) {
        try {
          const parsedProducts = JSON.parse(savedProducts);
          setProducts(parsedProducts);
          console.log('🔄 Produits synchronisés:', parsedProducts.length);
          // Mettre à jour les catégories après la synchronisation des produits
          setTimeout(() => updateAvailableCategories(), 100);
          
          // Synchroniser le stock avec le serveur après mise à jour
          if (parsedProducts && parsedProducts.length > 0) {
            syncStockWithServer(parsedProducts);
          }
        } catch (err) {
          console.error('Erreur lors de la synchronisation:', err);
        }
      }
    };

    window.addEventListener('productsUpdated', handleProductsUpdate);
    
    return () => {
      window.removeEventListener('productsUpdated', handleProductsUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Écouter les changements de catégories dans localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'productCategories') {
        console.log('📂 Catégories mises à jour dans localStorage');
        updateAvailableCategories();
      }
    };

    const handleCategoriesUpdate = (e) => {
      console.log('📂 Événement de mise à jour des catégories reçu:', e.detail);
      updateAvailableCategories();
    };

    // Écouter les changements de localStorage
    window.addEventListener('storage', handleStorageChange);
    
    // Écouter les événements personnalisés de mise à jour des catégories
    window.addEventListener('categoriesUpdated', handleCategoriesUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('categoriesUpdated', handleCategoriesUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sauvegarder les produits dans localStorage à chaque changement
  useEffect(() => {
    if (products.length > 0 && isInitialized) {
      localStorage.setItem('farmProducts', JSON.stringify(products));
      // Log seulement si c'est un changement significatif
      console.log('💾 Produits sauvegardés dans localStorage:', products.length);
    }
  }, [products, isInitialized]);

  // Mettre à jour les catégories quand les produits changent (séparé pour éviter les boucles)
  useEffect(() => {
    if (products.length > 0 && isInitialized) {
      updateAvailableCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, isInitialized]);

  // Ajouter un produit
  const addProduct = async (productData) => {
    setIsLoading(true);
    try {
      const newProduct = {
        ...productData,
        id: nextId,
        stock: productData.stock || 0,
        featured: productData.featured || false,
        createdAt: new Date().toISOString()
      };
      
      setProducts(prevProducts => [...prevProducts, newProduct]);
      setNextId(prevId => prevId + 1);
      setError(null);
      
      // Synchroniser avec Stripe et récupérer les IDs
      try {
        const syncResult = await stripeService.syncProducts([newProduct]);
        if (syncResult.syncedProducts && syncResult.syncedProducts.length > 0) {
          const syncedProduct = syncResult.syncedProducts[0];
          // Mettre à jour le produit avec les IDs Stripe
          setProducts(prevProducts => 
            prevProducts.map(p => 
              p.id === newProduct.id 
                ? {
                    ...p,
                    stripeProductId: syncedProduct.stripeProductId,
                    stripePriceId: syncedProduct.stripePriceId,
                    syncedAt: syncedProduct.syncedAt
                  }
                : p
            )
          );
          console.log('✅ Produit ajouté et synchronisé avec Stripe (ID:', syncedProduct.stripeProductId, ')');
        }
      } catch (stripeError) {
        console.warn('⚠️ Erreur lors de la synchronisation Stripe:', stripeError.message);
        // Le produit est quand même ajouté localement
      }
      
      return { success: true, product: newProduct };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Mettre à jour un produit
  const updateProduct = async (id, productData) => {
    setIsLoading(true);
    try {
      let updatedProduct;
      setProducts(prevProducts => {
        const updated = prevProducts.map(product => 
          product.id === id 
            ? { ...product, ...productData, updatedAt: new Date().toISOString() }
            : product
        );
        updatedProduct = updated.find(p => p.id === id);
        
        // Sauvegarder dans localStorage pour la persistance
        try {
          localStorage.setItem('farmProducts', JSON.stringify(updated));
          console.log('💾 Produits sauvegardés dans localStorage après mise à jour');
        } catch (storageError) {
          console.warn('⚠️ Erreur lors de la sauvegarde localStorage:', storageError);
        }
        
        return updated;
      });
      setError(null);
      
      // Synchroniser avec Stripe et mettre à jour les IDs si nécessaire
      if (updatedProduct) {
        try {
          const syncResult = await stripeService.syncProducts([updatedProduct]);
          if (syncResult.syncedProducts && syncResult.syncedProducts.length > 0) {
            const syncedProduct = syncResult.syncedProducts[0];
            // Mettre à jour le produit avec les nouveaux IDs Stripe
            setProducts(prevProducts => 
              prevProducts.map(p => 
                p.id === id 
                  ? {
                      ...p,
                      ...updatedProduct,
                      stripeProductId: syncedProduct.stripeProductId,
                      stripePriceId: syncedProduct.stripePriceId,
                      syncedAt: syncedProduct.syncedAt,
                      updatedAt: new Date().toISOString()
                    }
                  : p
              )
            );
            console.log('✅ Produit mis à jour et synchronisé avec Stripe');
          }
        } catch (stripeError) {
          console.warn('⚠️ Erreur lors de la synchronisation Stripe:', stripeError.message);
          // Le produit est quand même mis à jour localement
        }
      }
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Supprimer un produit
  const deleteProduct = async (id) => {
    setIsLoading(true);
    try {
      setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
      setError(null);
      
      // Supprimer sur Stripe en arrière-plan
      try {
        await stripeService.deleteProduct(id);
        console.log('✅ Produit supprimé sur Stripe');
      } catch (stripeError) {
        console.warn('⚠️ Erreur lors de la suppression Stripe:', stripeError.message);
      }
      
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Supprimer plusieurs produits
  const deleteManyProducts = (ids) => {
    setIsLoading(true);
    try {
      setProducts(prevProducts => prevProducts.filter(product => !ids.includes(product.id)));
      setError(null);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsLoading(false);
    }
  };

  // Obtenir un produit par ID
  const getProduct = (id) => {
    return products.find(product => product.id === parseInt(id));
  };

  // Obtenir les produits par catégorie
  const getProductsByCategory = (category) => {
    if (category === 'Tous' || !category) {
      return products;
    }
    return products.filter(product => product.category === category);
  };

  // Obtenir les produits vedettes
  const getFeaturedProducts = () => {
    return products.filter(product => product.featured);
  };

  // Obtenir les catégories uniques (retourne l'état actuel)
  const getCategories = () => {
    return availableCategories;
  };

  // Rechercher des produits
  const searchProducts = (query) => {
    if (!query) return products;
    return products.filter(product => 
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.category.toLowerCase().includes(query.toLowerCase()) ||
      product.description.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Rafraîchir les produits (pour synchronisation)
  const refreshProducts = () => {
    const savedProducts = localStorage.getItem('farmProducts');
    if (savedProducts) {
      try {
        const parsedProducts = JSON.parse(savedProducts);
        console.log('🔄 Rafraîchissement des produits:', parsedProducts.length, 'produits');
        setProducts(parsedProducts);
      } catch (err) {
        console.error('Erreur lors du rafraîchissement:', err);
      }
    } else {
      console.log('📦 Aucune donnée trouvée dans localStorage, utilisation des produits initiaux');
      setProducts(initialProducts);
    }
  };

  const value = {
    products,
    isLoading,
    error,
    availableCategories,
    addProduct,
    updateProduct,
    deleteProduct,
    deleteManyProducts,
    getProduct,
    getProductsByCategory,
    getFeaturedProducts,
    getCategories,
    searchProducts,
    refreshProducts
  };

  return (
    <ProductsContext.Provider value={value}>
      {children}
    </ProductsContext.Provider>
  );
};
